import moment from 'moment';

import {
  Agreement,
  AgreementLine,
  AgreementState,
  BuyableId,
  BuyableType,
  Invoice,
  InvoiceLine,
} from 'common/types/app-api/billing_bump/types';

export type FormattedLine = AgreementLine & {
  quantityLine?: Record<string, AgreementLine>;
  subitems: {[buyable_id: number]: AgreementLine};
};

export const sortAgreementLines = (a: Partial<AgreementLine>, b: Partial<AgreementLine>) => {
  // Prioritize items where buyable_type === BASE_FEATURE_PLAN
  if (a.buyable_type === `BASE_FEATURE_PLAN` && b.buyable_type !== `BASE_FEATURE_PLAN`) {
    return -1;
  }
  if (b.buyable_type === `BASE_FEATURE_PLAN` && a.buyable_type !== `BASE_FEATURE_PLAN`) {
    return 1;
  }

  // If both are BASE_FEATURE_PLAN, tiebreak by start date
  if (
    a.buyable_type === `BASE_FEATURE_PLAN` &&
    b.buyable_type === `BASE_FEATURE_PLAN` &&
    a.start_date &&
    b.start_date
  ) {
    return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
  }

  // Compare by start date first in ASCENDING order
  if (a.start_date && b.start_date) {
    const dateComparison = new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
    if (dateComparison !== 0) {
      return dateComparison;
    }
  }

  // If the start dates are the same, compare by total price
  // in DESCENDING order
  return Number(b.net_price || `0.00`) - Number(a.net_price || `0.00`);
};

type GetEarliestStartAndLatestEndDateReturnType = {
  earliestStartDate: string | undefined;
  latestEndDate: string | undefined;
};
export function getEarliestStartAndLatestEndDate(agreements: Agreement[]): GetEarliestStartAndLatestEndDateReturnType {
  let earliestStartDate: string | undefined = undefined;
  let latestEndDate: string | undefined = undefined;

  agreements.forEach((agreement) => {
    if (!earliestStartDate || new Date(agreement.start_date) < new Date(earliestStartDate)) {
      earliestStartDate = agreement.start_date;
    }

    if (!latestEndDate || new Date(agreement.planned_end_date) > new Date(latestEndDate)) {
      latestEndDate = agreement.planned_end_date;
    }
  });

  return {earliestStartDate, latestEndDate};
}

export type OverageInformation = {
  buyableId: BuyableId;
  overageStartOfMonth: string;
  overageEndOfMonth: string;
  amount: number;
  quantity: number;
  state: string;
};

export function parseOverageInformation(
  currentAgreementStartDate: string,
  invoiceData: Invoice[],
  historicalAgreements: Agreement[],
): Record<number, OverageInformation[]> {
  const agreementsInLastPeriod = historicalAgreements.filter(
    (agreement: Agreement) =>
      moment
        .utc(agreement.planned_end_date)
        .isSame(moment.utc(currentAgreementStartDate || ``).subtract(1, `days`), `day`) &&
      agreement.state === AgreementState.BILLING_COMPLETED,
  );

  const agreementLineIdToBuyableId: Record<number, number> = Object.fromEntries(
    agreementsInLastPeriod
      .reduce((lines: AgreementLine[], agreement) => lines.concat(agreement.agreement_lines), [])
      .filter((line: AgreementLine) => line.buyable_type === BuyableType.METERED)
      .map((line) => [line.id, line.buyable_id]),
  );

  const overageInformation: Record<number, OverageInformation[]> = {};
  const adjustedInvoiceLineIds: Record<number, InvoiceLine[]> = {};

  // First get the adjusted invoice lines
  invoiceData.forEach((invoice) => {
    invoice.line_items
      .filter((line) => line.metadata?.adjusted_invoice_line_item_id)
      .forEach((line) => {
        const adjustedId = line.metadata?.adjusted_invoice_line_item_id;
        if (adjustedId) {
          if (adjustedId in adjustedInvoiceLineIds) {
            adjustedInvoiceLineIds[adjustedId].push(line);
          } else {
            adjustedInvoiceLineIds[adjustedId] = [line];
          }
        }
      });
  });

  // Then go through overage lines and link the adjusted line if exists
  invoiceData.forEach((invoice) => {
    invoice.line_items
      .filter((line) => line.metadata?.usage_month)
      .forEach((line) => {
        const buyableId = agreementLineIdToBuyableId[line.agreement_line_id || 1];
        if (buyableId) {
          const usageMonthMoment = moment.utc(line.metadata?.usage_month || ``, `MMMM YYYY`);
          const thisOverageInformation: OverageInformation = {
            buyableId,
            overageStartOfMonth: usageMonthMoment.startOf(`month`).format(`YYYY-MM-DD`),
            overageEndOfMonth: usageMonthMoment.endOf(`month`).format(`YYYY-MM-DD`),
            amount: line.amount - (line.sales_tax_amount || 0),
            quantity: line.quantity || 0,
            state: invoice.state,
          };
          if (adjustedInvoiceLineIds[line.id]) {
            const adjustementLines = adjustedInvoiceLineIds[line.id];
            const adjustedAmount =
              adjustementLines.reduce((acc, adjustedLine) => acc + (adjustedLine.amount || 0), 0) * -1;
            if (adjustedAmount === line.amount) {
              thisOverageInformation.state = `Waived`;
            }
          }
          if (buyableId in overageInformation) {
            overageInformation[buyableId].push(thisOverageInformation);
          } else {
            overageInformation[buyableId] = [thisOverageInformation];
          }
        }
      });
  });

  return overageInformation;
}
